import { Tooltip } from '@maxtropy/components';
import { apiV2LoadDispatchCenterDevicePowerStatusPost } from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import iconCalcium from '../../imgs/icon_calcium.png';
import iconRun from '../../imgs/icon_run.png';
import iconStop from '../../imgs/icon_stop.png';
import { formatPower } from '../../utils';
import SectionTitle from '../SectionTitle';
import styles from './index.module.scss';

const formatCount = (value?: number) => (value == null ? '--' : `${value}台`);

const DevicePowerStatus = () => {
  const { data } = useRequest(() => apiV2LoadDispatchCenterDevicePowerStatusPost({}), {
    pollingInterval: 60 * 1000,
  });
  const groups = [
    { name: '电石炉组', furnaceName: '电石炉', totalName: '电石', data: data?.calciumCarbideGroup },
    { name: '硅铁炉组', furnaceName: '硅铁炉', totalName: '硅铁', data: data?.ferrosiliconGroup },
  ];
  const totalLoad = formatPower(data?.totalLoad);

  return (
    <section className={styles.panel}>
      <div className={styles.deviceHeader}>
        <SectionTitle>负荷侧设备功率状态</SectionTitle>
        <div className={styles.headerMetrics}>
          <div className={styles.deviceStats}>
            <span>
              <span className={styles.metricContent}>
                <span className={styles.metricLabel}>设备总数:</span>
                <Tooltip title={formatCount(data?.deviceTotal)}>
                  <b>{formatCount(data?.deviceTotal)}</b>
                </Tooltip>
              </span>
            </span>
            <span className={styles.runningMetric}>
              <span className={styles.metricContent}>
                <span className={styles.metricLabel}>运行设备:</span>
                <Tooltip title={formatCount(data?.runningDeviceCount)}>
                  <b className={styles.green}>{formatCount(data?.runningDeviceCount)}</b>
                </Tooltip>
              </span>
            </span>
            <span className={styles.stoppedMetric}>
              <span className={styles.metricContent}>
                <span className={styles.metricLabel}>停机设备:</span>
                <Tooltip title={formatCount(data?.stoppedDeviceCount)}>
                  <b className={styles.red}>{formatCount(data?.stoppedDeviceCount)}</b>
                </Tooltip>
              </span>
            </span>
          </div>
          <div className={styles.totalLoad}>
            <span className={styles.totalLoadContent}>
              <span className={styles.totalLoadLabel}>总负荷({totalLoad.unit}):</span>
              <Tooltip title={totalLoad.text}>
                <em>{totalLoad.value}</em>
              </Tooltip>
            </span>
          </div>
        </div>
      </div>

      <div className={styles.groups}>
        {groups.map(group => {
          const furnaces = group.data?.furnaceList ?? [];
          const furnaceCount = group.data ? group.data.furnaceCount ?? furnaces.length : undefined;
          const platformTotalLoad = formatPower(group.data?.platformTotalLoad);
          const summaries = [
            { label: '炉台总负荷', value: group.data?.platformTotalLoad },
            { label: `${group.furnaceName}动力变总负荷`, value: group.data?.powerTransformerTotalLoad },
            { label: `${group.totalName}总负荷`, value: group.data?.furnaceTotalLoad },
          ];

          return (
            <article className={styles.group} key={group.name}>
              <div className={styles.groupTitle}>
                <span>
                  <img className={styles.groupIcon} src={iconCalcium} alt="" />
                  {group.name} ({formatCount(furnaceCount)})
                </span>
                <span>
                  总计:
                  <strong>{platformTotalLoad.text}</strong>
                </span>
              </div>

              <div className={styles.groupSummary}>
                {summaries.map(item => {
                  const formattedPower = formatPower(item.value);
                  return (
                    <div key={item.label}>
                      <span>{item.label}</span>
                      <Tooltip title={formattedPower.text}>
                        <strong>
                          {formattedPower.value}
                          {item.value != null && <small> {formattedPower.unit}</small>}
                        </strong>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>

              <div className={styles.deviceGrid}>
                {furnaces.map((furnace, deviceIndex) => {
                  const stopped = furnace.runningStatus === 1;
                  const deviceName = furnace.deviceName ?? furnace.deviceCode ?? `炉台${deviceIndex + 1}`;
                  const formattedPower = formatPower(furnace.power);

                  return (
                    <div
                      className={`${styles.device} ${stopped ? styles.stopped : ''}`}
                      key={furnace.deviceId ?? furnace.deviceCode ?? `${group.name}-${deviceIndex}`}
                    >
                      <Tooltip title={deviceName}>
                        <span>{deviceName}</span>
                      </Tooltip>
                      {furnace.priority && <i>优先调控</i>}
                      <Tooltip title={formattedPower.text}>
                        <strong>
                          {formattedPower.value}
                          {furnace.power != null && <small> {formattedPower.unit}</small>}
                        </strong>
                      </Tooltip>
                      {stopped ? (
                        <img className={styles.stopIcon} src={iconStop} alt="停机" />
                      ) : (
                        <img className={styles.runIcon} src={iconRun} alt="运行" />
                      )}
                    </div>
                  );
                })}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default DevicePowerStatus;
